import { FC, useEffect, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import { VStack, Input, useToast, Switch } from "@chakra-ui/react";
import {
    TimesheetType as Entity,
    TimesheetTypeApi as EntityApi,
} from "../../../apis";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { AppFormContainer } from "../../../containers";
import { AppFormControl } from "../../../components";
import { APPC } from "../../../config";

export const TimesheetTypeForm: FC<{
    formContainerState: FormContainerStateReturnType;
}> = ({ formContainerState }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();

    // state & const
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Entity>(new Entity());

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<Entity>({
        resolver: yupResolver(schema(t)),
        mode: "onSubmit",
    });

    // handler
    const onSubmitHandler = (formData: Entity) => {
        setLoading(true);
        EntityApi.createOrUpdate<Entity, Entity>(
            formContainerState.entityId,
            formData
        )
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.entityId) {
            reset();
            setData(new Entity());
            return;
        }
        setLoading(true);
        EntityApi.getItem<Entity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    setData(response);
                }
            })
            .finally(() => setLoading(false));
    }, [formContainerState.entityId]);

    return (
        <AppFormContainer
            title={t("padm.TimesheetTypeForm:text.pageTitle")}
            state={formContainerState}
            type={APPC.FORM_CONTAINER}
        >
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"}>
                    <AppFormControl
                        label={t("ent.TimesheetType:name.label")}
                        isInvalid={!!errors.name}
                        message={errors.name?.message}
                        isLoading={loading}
                    >
                        <Input
                            type="text"
                            placeholder={t(
                                "ent.TimesheetType:name.placeholder"
                            )}
                            {...register("name")}
                            defaultValue={data.name}
                        />
                    </AppFormControl>
                    <AppFormControl
                        label={t("ent.TimesheetType:isCountable.label")}
                        isLoading={loading}
                    >
                        <Switch
                            {...register("isCountable")}
                            defaultChecked={data.isCountable}
                        />
                    </AppFormControl>
                </VStack>
            </form>
        </AppFormContainer>
    );
};
